import { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Divider,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import * as Diff from 'diff';
import { ThemeContext, SocketContext } from '../Context';
import GetUserAvatar from '../tool/GetUserAvatar';
import RestoreConfirmDialog from './RestoreConfirmDialog';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';

// Fonction pour décoder les entités HTML et nettoyer le texte
const decodeAndCleanHtml = (html) => {
  if (!html) return '';
  
  // Créer un élément temporaire pour décoder les entités HTML
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Récupérer le texte décodé (sans balises HTML)
  return doc.body.textContent || '';
};

// Composant pour afficher le diff visuel
// Compare le contenu actuel avec le contenu précédent
function DiffView({ previousText, currentText, isDarkMode }) {
  if (!previousText && !currentText) {
    return (
      <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
        Aucun contenu
      </Typography>
    );
  }

  // Décoder les entités HTML et nettoyer avant de comparer
  const cleanPrevious = decodeAndCleanHtml(previousText);
  const cleanCurrent = decodeAndCleanHtml(currentText);

  // Générer le diff : on compare previous (avant) avec current (maintenant)
  // added = présent dans current mais pas dans previous (nouveau)
  // removed = présent dans previous mais pas dans current (supprimé)
  const diff = Diff.diffWords(cleanPrevious, cleanCurrent);

  return (
    <Box
      sx={{
        fontFamily: 'monospace',
        fontSize: '13px',
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        p: 2,
        bgcolor: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.02)',
        borderRadius: 1,
        maxHeight: '400px',
        overflow: 'auto'
      }}
    >
      {diff.map((part, index) => {
        if (part.added) {
          // Ajouté dans la version actuelle (nouveau contenu) = VERT
          return (
            <span
              key={index}
              style={{
                backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)',
                color: isDarkMode ? '#81c784' : '#2e7d32',
                textDecoration: 'none',
                padding: '0 2px',
                borderRadius: '2px'
              }}
            >
              {part.value}
            </span>
          );
        }
        if (part.removed) {
          // Supprimé de l'ancienne version (n'existe plus) = ROUGE BARRÉ
          return (
            <span
              key={index}
              style={{
                backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)',
                color: isDarkMode ? '#e57373' : '#c62828',
                textDecoration: 'line-through',
                padding: '0 2px',
                borderRadius: '2px'
              }}
            >
              {part.value}
            </span>
          );
        }
        return <span key={index}>{part.value}</span>;
      })}
    </Box>
  );
}

// Composant pour afficher le contenu sans diff
function ContentView({ content, isDarkMode }) {
  // Supprimer les balises HTML pour l'aperçu
  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.02)',
        borderRadius: 1,
        maxHeight: '400px',
        overflow: 'auto'
      }}
    >
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: 1.6
        }}
      >
        {stripHtml(content) || <em style={{ color: 'gray' }}>Contenu vide</em>}
      </Typography>
    </Box>
  );
}

function HistoryPreview({ open, entry, onClose, reportData }) {
  const { theme } = useContext(ThemeContext);
  const isDarkMode = theme === 'dark';
  const { socket } = useContext(SocketContext);

  const [loading, setLoading] = useState(false);
  const [entryDetails, setEntryDetails] = useState(null);
  const [currentContent, setCurrentContent] = useState(null);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // Charger les détails de l'entrée
  useEffect(() => {
    const token = sessionStorage.getItem('authToken');
    if (!open || !entry || !reportData?._id || !token) return;

    const loadDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `/api/history/${reportData._id}/entry/${entry._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setEntryDetails(response.data);

        // Trouver le contenu actuel de la rubrique
        const currentRubric = reportData.rubrics?.find(r => r.id === entry.rubricId);
        setCurrentContent(currentRubric ? {
          title: currentRubric.title,
          content: currentRubric.content
        } : null);

      } catch (err) {
        console.error('Erreur lors du chargement des détails:', err);
        setError('Impossible de charger les détails de cette modification');
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [open, entry, reportData?._id, reportData?.rubrics]);

  // Restaurer la version
  const handleRestore = async () => {
    if (!entryDetails || !reportData?._id) return;

    const token = sessionStorage.getItem('authToken');
    if (!token) return;

    setRestoring(true);
    setError(null);

    try {
      await axios.post(
        `/api/history/${reportData._id}/restore/${entry._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Log de la restauration avec ancien et nouveau contenu
      logAction(LOG_ACTIONS.HISTORY_RESTORE, formatReportNumber(reportData.number, reportData.year), {
        rubricTitle: entryDetails?.rubricTitle || null,
        changeType: entryDetails?.changeType || null,
        restoredFrom: entryDetails?.modifiedAt || null,
        // Contenu actuel avant restauration (sera remplacé)
        ancienContenu: currentContent?.content?.substring(0, 1000) || null,
        ancienTitre: currentContent?.title || null,
        // Contenu restauré (version précédente qui devient la nouvelle)
        nouveauContenu: entryDetails?.previousContent?.substring(0, 1000) || null,
        nouveauTitre: entryDetails?.previousTitle || null
      });

      setRestoreDialogOpen(false);
      onClose(true); // Signaler qu'un rafraîchissement est nécessaire

    } catch (err) {
      console.error('Erreur lors de la restauration:', err);
      setError('Impossible de restaurer cette version');
    } finally {
      setRestoring(false);
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? '#1a1a2e' : '#fff',
            minHeight: '60vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CompareArrowsIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Détail de la modification
              </Typography>
            </Box>
            <IconButton onClick={() => onClose(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : entryDetails ? (
            <Box>
              {/* Alerte pour les rubriques supprimées */}
              {entryDetails.changeType === 'delete' && (
                <Alert 
                  severity="warning" 
                  sx={{ mb: 2 }}
                  icon={<RestoreIcon />}
                >
                  <Typography variant="body2" fontWeight="bold">
                    Cette rubrique a été supprimée
                  </Typography>
                  <Typography variant="body2">
                    Vous pouvez la restaurer en cliquant sur le bouton ci-dessous.
                    Elle sera ajoutée à la fin du document.
                  </Typography>
                </Alert>
              )}

              {/* Info sur la modification */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: entryDetails.changeType === 'delete' 
                    ? (isDarkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)')
                    : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'),
                  borderRadius: 2,
                  border: entryDetails.changeType === 'delete' 
                    ? `1px solid ${isDarkMode ? '#e57373' : '#ef9a9a'}`
                    : 'none'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <GetUserAvatar matricule={entryDetails.modifiedBy} />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(entryDetails.modifiedAt)}
                  </Typography>
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    textDecoration: entryDetails.changeType === 'delete' ? 'line-through' : 'none'
                  }}>
                    <strong>Rubrique :</strong> {entryDetails.rubricTitle || 'Sans titre'}
                  </Typography>
                </Box>
              </Paper>

              {/* Onglets */}
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab
                  icon={<CompareArrowsIcon />}
                  iconPosition="start"
                  label="Différences"
                />
                <Tab
                  icon={<VisibilityIcon />}
                  iconPosition="start"
                  label="Version précédente"
                />
              </Tabs>

              {/* Contenu des onglets */}
              {tabValue === 0 && (
                <Box>
                  {/* Diff du titre si différent */}
                  {entryDetails.previousTitle !== currentContent?.title && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Titre
                      </Typography>
                      <DiffView
                        previousText={entryDetails.previousTitle}
                        currentText={currentContent?.title}
                        isDarkMode={isDarkMode}
                      />
                    </Box>
                  )}

                  {/* Diff du contenu */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Contenu
                    </Typography>
                    <DiffView
                      previousText={entryDetails.previousContent}
                      currentText={currentContent?.content}
                      isDarkMode={isDarkMode}
                    />
                  </Box>

                  {/* Légende */}
                  <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '2px',
                          bgcolor: isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Contenu de la version précédente
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '2px',
                          bgcolor: isDarkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Contenu ajouté depuis
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {tabValue === 1 && (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Titre
                    </Typography>
                    <ContentView
                      content={entryDetails.previousTitle}
                      isDarkMode={isDarkMode}
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Contenu
                    </Typography>
                    <ContentView
                      content={entryDetails.previousContent}
                      isDarkMode={isDarkMode}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => onClose(false)} color="inherit">
            Fermer
          </Button>
          <Tooltip title={entryDetails?.changeType === 'delete' 
            ? "Récupérer cette rubrique supprimée" 
            : "Restaurer cette version de la rubrique"
          }>
            <Button
              variant="contained"
              color={entryDetails?.changeType === 'delete' ? 'success' : 'warning'}
              startIcon={<RestoreIcon />}
              onClick={() => setRestoreDialogOpen(true)}
              disabled={loading || !entryDetails}
            >
              {entryDetails?.changeType === 'delete' 
                ? 'Récupérer cette rubrique' 
                : 'Restaurer cette version'
              }
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de restauration */}
      <RestoreConfirmDialog
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
        onConfirm={handleRestore}
        loading={restoring}
        rubricTitle={entryDetails?.rubricTitle}
        modifiedAt={entryDetails?.modifiedAt}
        modifiedByName={entryDetails?.modifiedByName}
        isDeleted={entryDetails?.changeType === 'delete'}
      />
    </>
  );
}

export default HistoryPreview;
